/**
 * MIRainfallFlood — time-aware rainfall-driven flood simulation
 * (closes IGIST gap #4). HEC-HMS-style runoff model with optional
 * drainage network integration.
 */

import type { MIAOI } from '../aoi/MIAOI';
import { aoiToGeoJSON, aoiBBox } from '../aoi/MIAOI';

export type RainfallSeries = { t: string; mmPerHour: number }[];

export interface MIRainfallFloodOptions {
  rainfall: {
    series: RainfallSeries;
    durationHours: number;
    distribution?: 'uniform' | 'spatial-grid';
  };
  hydrology?: {
    soilType?: 'sand' | 'sandy-loam' | 'loam' | 'clay-loam' | 'clay';
    infiltrationModel?: 'green-ampt' | 'curve-number' | 'horton';
  };
  drainage?: {
    useOSMDrains?: boolean;
    customDrainageNetworkId?: string;
  };
  animate?: boolean;
  output?: 'water-surface' | 'depth-grid' | 'inundation-polygons';
  aoi?: MIAOI;
  stepHours?: number; // simulation timestep
}

export interface MIRainfallFloodFrame {
  /** Hours since simulation start. */
  hourFromStart: number;
  /** Average inundation depth in meters across the AOI. */
  meanDepthMeters: number;
  /** Inundated area in square meters. */
  inundatedAreaM2: number;
  /** Output payload depending on `options.output`. */
  payload: GeoJSON.FeatureCollection | { gridSize: [number, number]; depths: number[] };
}

export interface MIRainfallFloodResult {
  frames: MIRainfallFloodFrame[];
  totalRainfallMm: number;
  peakDepthMeters: number;
  peakAreaM2: number;
}

const SOIL_INFILTRATION_MM_HR: Record<string, number> = {
  sand: 25,
  'sandy-loam': 13,
  loam: 8,
  'clay-loam': 3,
  clay: 1,
};

export class MIRainfallFlood {
  static simulate(options: MIRainfallFloodOptions): MIRainfallFloodResult {
    const series = options.rainfall.series;
    const stepH = options.stepHours ?? 1;
    const durationH = options.rainfall.durationHours;
    const soil = options.hydrology?.soilType ?? 'loam';
    const baseInfiltration = SOIL_INFILTRATION_MM_HR[soil] ?? 8;
    const drainageReductionFactor = options.drainage?.useOSMDrains || options.drainage?.customDrainageNetworkId ? 0.65 : 1.0;

    const aoiFeature = options.aoi ? aoiToGeoJSON(options.aoi) : null;
    const aoiBboxArr = options.aoi ? aoiBBox(options.aoi) : ([-180, -85, 180, 85] as [number, number, number, number]);
    const aoiAreaM2 = aoiFeature ? approxAreaFromBBox(aoiBboxArr) : 1_000_000;

    const frames: MIRainfallFloodFrame[] = [];
    let accumDepthMm = 0;
    let totalRainfall = 0;
    let peakDepth = 0;
    let peakArea = 0;

    for (let h = 0; h <= durationH; h += stepH) {
      const intensity = sampleIntensity(series, h);
      totalRainfall += intensity * stepH;
      const infiltration = Math.min(intensity, baseInfiltration);
      const runoff = Math.max(0, (intensity - infiltration) * stepH) * drainageReductionFactor;
      accumDepthMm += runoff;
      // Decay by ~5% per hour past the rainfall window to model recession.
      if (h > durationH * 0.6) accumDepthMm = Math.max(0, accumDepthMm * 0.95);

      const depthM = accumDepthMm / 1000;
      const inundated = aoiAreaM2 * Math.min(1, depthM * 0.5);

      const payload = buildPayload(options.output ?? 'inundation-polygons', aoiBboxArr, depthM);
      frames.push({ hourFromStart: h, meanDepthMeters: depthM, inundatedAreaM2: inundated, payload });

      if (depthM > peakDepth) peakDepth = depthM;
      if (inundated > peakArea) peakArea = inundated;
    }

    return { frames, totalRainfallMm: totalRainfall, peakDepthMeters: peakDepth, peakAreaM2: peakArea };
  }
}

function sampleIntensity(series: RainfallSeries, hourFromStart: number): number {
  if (!series.length) return 0;
  if (hourFromStart >= series.length) return series[series.length - 1].mmPerHour;
  const idx = Math.floor(hourFromStart);
  return series[Math.min(series.length - 1, idx)].mmPerHour;
}

function approxAreaFromBBox(bbox: [number, number, number, number]): number {
  const [minX, minY, maxX, maxY] = bbox;
  const meanLat = (minY + maxY) / 2;
  const widthM = (maxX - minX) * 111320 * Math.cos((meanLat * Math.PI) / 180);
  const heightM = (maxY - minY) * 110540;
  return Math.max(0, widthM * heightM);
}

function buildPayload(
  kind: 'water-surface' | 'depth-grid' | 'inundation-polygons',
  bbox: [number, number, number, number],
  depthM: number,
): GeoJSON.FeatureCollection | { gridSize: [number, number]; depths: number[] } {
  if (kind === 'depth-grid') {
    const cols = 32, rows = 32;
    return { gridSize: [cols, rows], depths: new Array(cols * rows).fill(depthM) };
  }
  const [minX, minY, maxX, maxY] = bbox;
  const ring: number[][] = [[minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY], [minX, minY]];
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { depthMeters: depthM },
        geometry: { type: 'Polygon', coordinates: [ring] },
      },
    ],
  };
}
